import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpErrorToastGroup, HttpErrorToastService } from './http-error-toast.service';

type CopyState = 'latest' | null;

const COPIED_STATE_DURATION_MS = 3_000;
const TOAST_EXIT_DURATION_MS = 300;
const TOAST_EXIT_STAGGER_MS = 100;

@Component({
  selector: 'app-http-error-toast-host',
  templateUrl: './http-error-toast-host.component.html',
  styleUrls: ['./http-error-toast-host.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HttpErrorToastHostComponent {
  readonly groups$: Observable<readonly HttpErrorToastGroup[]>;
  private readonly copyStates = new Map<string, CopyState>();
  private readonly leavingKeys = new Set<string>();
  private allVisibleCopied = false;
  private toolbarLeaving = false;

  constructor(
    private readonly errorToastService: HttpErrorToastService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.groups$ = this.errorToastService.groups$;
  }

  isCopied(group: HttpErrorToastGroup): boolean {
    return this.copyStates.get(group.key) === 'latest';
  }

  isLeaving(group: HttpErrorToastGroup): boolean {
    return this.leavingKeys.has(group.key);
  }

  dismiss(group: HttpErrorToastGroup): void {
    if (this.leavingKeys.has(group.key)) {
      return;
    }

    this.leavingKeys.add(group.key);
    this.cdr.markForCheck();
    window.setTimeout(() => {
      this.errorToastService.dismiss(group.key);
      this.leavingKeys.delete(group.key);
      this.cdr.markForCheck();
    }, TOAST_EXIT_DURATION_MS);
  }

  dismissAll(groups: readonly HttpErrorToastGroup[]): void {
    if (this.toolbarLeaving) {
      return;
    }

    this.toolbarLeaving = true;
    this.cdr.markForCheck();
    groups.forEach((group, index) => {
      window.setTimeout(
        () => this.dismiss(group),
        TOAST_EXIT_STAGGER_MS + index * TOAST_EXIT_STAGGER_MS
      );
    });
    window.setTimeout(() => {
      this.toolbarLeaving = false;
      this.cdr.markForCheck();
    }, TOAST_EXIT_DURATION_MS + groups.length * TOAST_EXIT_STAGGER_MS);
  }

  async copyLatest(group: HttpErrorToastGroup): Promise<void> {
    await this.copy(group, this.errorToastService.latestLog(group));
  }

  async copyAllVisible(groups: readonly HttpErrorToastGroup[]): Promise<void> {
    try {
      await this.writeToClipboard(this.errorToastService.allVisibleLogs(groups));
      this.allVisibleCopied = true;
      this.cdr.markForCheck();
      window.setTimeout(() => {
        this.allVisibleCopied = false;
        this.cdr.markForCheck();
      }, COPIED_STATE_DURATION_MS);
    } catch {
      // Clipboard access can be denied by the browser; the error toast must remain usable.
    }
  }

  get isAllVisibleCopied(): boolean {
    return this.allVisibleCopied;
  }

  get isToolbarLeaving(): boolean {
    return this.toolbarLeaving;
  }

  trackByKey(_: number, group: HttpErrorToastGroup): string {
    return group.key;
  }

  private async copy(group: HttpErrorToastGroup, value: string): Promise<void> {
    try {
      await this.writeToClipboard(value);
      this.copyStates.set(group.key, 'latest');
      this.cdr.markForCheck();
      window.setTimeout(() => {
        if (this.copyStates.get(group.key) === 'latest') {
          this.copyStates.delete(group.key);
          this.cdr.markForCheck();
        }
      }, COPIED_STATE_DURATION_MS);
    } catch {
      // Clipboard access can be denied by the browser; the error toast must remain usable.
    }
  }

  private async writeToClipboard(value: string): Promise<void> {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    textarea.remove();

    if (!copied) {
      throw new Error('Clipboard copy failed.');
    }
  }
}
